/* eslint-disable @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { Observable } from 'rxjs'
import { catchError, filter, finalize, first, map, switchMap, takeUntil, tap } from 'rxjs/operators'
import { concat, forkJoin, of, Subject } from 'rxjs'
import { TranslateService } from '@ngx-translate/core'
import dayjs from 'dayjs'

import { Client, ClientStatus } from '@shared/models/client.model'
import { ClientsService } from '@app/modules/admin/clients/clients.service'
import { StatusHistoryDialogComponent } from '@app/modules/admin/shared/status-history-dialog/status-history-dialog.component'
import { User } from '@shared/models/user.model'
import { DataService } from '@shared/services/data.service'
import { clientStatuses } from '@app/core/constants/clientStatuses'
import { PermissionsService } from '@shared/services/permissions.service'
import { AdminTeamService } from '@app/modules/admin/admin-team/admin-team.service'
import { takeFormSnapshot } from '@core/helpers/take-form-snapshot'
import { DialogService } from '@shared/dialogs/dialog.service'
import { handleFormErrors } from '@app/core/helpers/handle-form-errors'
import { RoleType } from '@app/core/types/role.type'
import { AuthService } from '@app/modules/auth/auth.service'
import { Timezone } from '@shared/models/timezone.model'
import { Plan } from '@shared/models/plan.model'
import { TrackedUnsavedForm, UnsavedFormTracker } from '@core/services/unsaved-form-tracker.service'
import { ResellerService } from '@app/modules/reseller/shared/services/reseller.service'
import { ResellerUser } from '@app/shared/models/reseller.model'
import { PermissionSourceType, PermissionsSource } from '@core/types/permissions.source'
import { NoteHistory } from '@shared/models/note.model'
import { History } from '@shared/models/history.model'
import { PagesData } from '@shared/models/pages-data.model'
import { SettingsService } from '../settings/settings.service'
import { XeroContact, XeroGetContactsParams } from '@shared/models/billing.model'

@Component({
  selector: 'lin-client-form',
  templateUrl: './client-form.component.html',
  styleUrls: ['./client-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ClientFormComponent implements OnInit, OnDestroy, TrackedUnsavedForm {
  form: FormGroup
  formError: string
  isSubmitting = false
  onDestroy$ = new Subject<void>()
  clientId: string | number
  client: Client
  timezones: Timezone[]
  isLoaded = false
  adminNotesHistory: NoteHistory[]
  clientNotesHistory: NoteHistory[]
  plans: Plan[]

  getXeroContactsRequest = (params?: XeroGetContactsParams) =>
    this.clientsService.getXeroContacts(params, this.clientXeroContact?.contactId)
  xeroContacts: XeroContact[]
  clientXeroContact: XeroContact

  users: User[]
  countries: { key: string; value: string }[] = []
  user: User
  salesRepresentatives: User[] | ResellerUser[]
  accountManagers: User[] | ResellerUser[]
  statuses = clientStatuses
  formStatuses = clientStatuses
  isFormChanged: ReturnType<typeof takeFormSnapshot>

  readonly PermissionsSource = PermissionsSource
  readonly required = new Set<string>()

  get isEditMode(): boolean {
    return this.hasPermission(PermissionsSource['CONFIGURATION.COMPANY_INFO.EDIT'])
  }

  get isStatusMode(): boolean {
    return this.hasPermission(PermissionsSource['CONFIGURATION.COMPANY_INFO.STATUS_HISTORY'])
  }

  get isNotesMode(): boolean {
    return this.hasPermission(PermissionsSource['CONFIGURATION.COMPANY_INFO.NOTES_HISTORY'])
  }

  get isContractMonth(): boolean {
    return this.settingsService.isBilling || this.authService.getCurrentUser().isAdminOrReseller()
  }

  get isManager(): boolean {
    return (<string[]>[
      RoleType.AccountManager,
      RoleType.SalesRepresentative,
      RoleType.ResellerAccountManager,
      RoleType.ResellerSalesRepresentative,
    ]).includes(this.authService.getCurrentUser().role.name)
  }

  get showChevronAccountId(): boolean {
    return this.user.team?.options?.isChevron || (this.user.isAdmin() && this.client?.team?.options?.isChevron)
  }

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private clientsService: ClientsService,
    private translateService: TranslateService,
    private dialog: DialogService,
    private dataService: DataService,
    private adminTeamService: AdminTeamService,
    private permissionsService: PermissionsService,
    private authService: AuthService,
    private unsavedFormTrackerService: UnsavedFormTracker,
    private readonly resellerService: ResellerService,
    private readonly settingsService: SettingsService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.unsavedFormTrackerService.track(this)
    this.clientId = this.route.parent.snapshot.params.id as string
    const resellerId = this.route.parent.snapshot.params.resellerId as number

    const team$ = this.authService.currentUser$.pipe(
      first(),
      tap((user: User) => {
        this.user = user
        resellerId && (this.user.team.resellerId = resellerId)
        user.isClient() && (this.clientId = user.team.clientId)
        this.createForm()
      }),
      switchMap((user) => this.fetchClientsOrClientManagers(user)),
    )

    const timezones$ = this.dataService.getTimezones()
    const plans$ = this.dataService.getPlans()
    const countries$ = this.dataService.getCountryList()

    forkJoin([timezones$, plans$, countries$, team$])
      .pipe(
        tap(([timezones, plans, countries]) => {
          this.timezones = timezones
          this.plans = plans
          Object.entries(countries).forEach(([key, value]) => this.countries.push({ key, value }))
        }),
        filter(() => this.settingsService.isXeroEnabled),
        switchMap(() => this.getXeroContactsRequest({ page: 1 })),
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.isLoaded = true
          this.cdr.markForCheck()
        }),
      )
      .subscribe((xeroContacts) => (this.xeroContacts = xeroContacts))

    this.clientId && this.getNotes()
  }

  fetchClientsOrClientManagers(user: User) {
    if (user.isClient() || this.clientId) {
      return user.isReseller() || user.team.resellerId ? concat(this.fetchClients(), this.fetchResellerManagers()) : this.fetchClients()
    }
    return user.isReseller() ? this.fetchResellerManagers() : this.fetchClientManagers()
  }

  fetchClients() {
    const action$ = this.user.isAdmin() ? this.fetchClientManagers() : of(null)
    const isTeamMember = ({ team }: User) => team.id === this.client.team.id

    // TODO(BE-contract): remove catchError once BE implements `/xero/*` and `/clients/:id/users?role=admin`.
    // See local-docs/reports/admin-api-be-contract.md §8.
    const getXeroContact$ = this.settingsService.isXeroEnabled
      ? this.getClientXeroContact(this.clientId).pipe(catchError(() => of(null)))
      : of(null)

    return action$.pipe(
      switchMap(() => this.clientsService.getClient(this.clientId)),
      tap((client) => (this.client = client)),
      switchMap(() =>
        forkJoin([
          this.clientsService
            .getClientUsers(this.clientId, 0, 1000, null, { role: RoleType.Admin }, ['id', 'name', 'surname', 'email', 'team'])
            .pipe(catchError(() => of({ data: [] as User[] } as PagesData<User>))),
          getXeroContact$,
        ]),
      ),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      map(([users, xeroContact]) => [users.data, xeroContact]),
      map(([users, xeroContact]: [User[], XeroContact[]]) => [users.filter(isTeamMember), xeroContact]),
      tap(([users, xeroContact]: [User[], XeroContact[]]) => {
        this.users = users
        this.clientXeroContact = xeroContact ? xeroContact[0] : null
      }),
      tap(() => this.setFormValues()),
    )
  }

  fetchClientManagers() {
    return forkJoin([
      this.adminTeamService.getAdminTeam(0, 1000, null, { role: RoleType.SalesRepresentative }, ['id', 'name', 'surname', 'status']).pipe(
        tap((salesRepresentatives) => {
          const isNotBlocked = ({ status }) => status !== ClientStatus.Blocked
          this.salesRepresentatives = salesRepresentatives.data.filter(isNotBlocked)
        }),
      ),
      this.adminTeamService.getAdminTeam(0, 1000, null, { role: RoleType.AccountManager }, ['id', 'name', 'surname', 'status']).pipe(
        tap((accountManagers) => {
          const isNotBlocked = ({ status }) => status !== ClientStatus.Blocked
          this.accountManagers = accountManagers.data.filter(isNotBlocked)
        }),
      ),
    ])
  }

  fetchResellerManagers() {
    const createResellerUser = (user: ResellerUser) => new ResellerUser(user)
    const requestFields: (keyof ResellerUser)[] = ['id', 'name', 'surname', 'email', 'team']

    return forkJoin([
      this.resellerService
        .getResellerUsers(this.user.team.resellerId, 0, 1000, null, { role: RoleType.ResellerSalesRepresentative }, requestFields)
        .pipe(
          map((users) => users.data),
          tap((users: ResellerUser[]) => (this.salesRepresentatives = users.map(createResellerUser))),
        ),
      this.resellerService
        .getResellerUsers(this.user.team.resellerId, 0, 1000, null, { role: RoleType.ResellerAccountManager }, requestFields)
        .pipe(
          map((users) => users.data),
          tap((users: ResellerUser[]) => (this.accountManagers = users.map(createResellerUser))),
        ),
    ])
  }

  hasPermission(permissionType: PermissionSourceType) {
    return this.permissionsService.hasPermissionSync(permissionType)
  }

  createForm() {
    this.form = this.formBuilder.group({
      name: '',
      legalName: '',
      country: '',
      taxNr: '',
      chevronAccountId: '',
      legalAddress: '',
      billingAddress: '',
      status: '',
      plan: '',
      keyContact: '',
      accountingContact: '',
      timezone: '',
      salesRepresentative: '',
      accountManager: '',
      clientNote: '',
      adminNote: '',
      expirationDate: '',
      language: '',
    })

    this.setRequired('name')
    this.setRequired('status')
    this.setRequired('plan')

    if (this.isManager) {
      this.form.get('salesRepresentative').disable()
      this.form.get('accountManager').disable()
    }

    if (!this.clientId) {
      this.form.removeControl('keyContact')
      this.form.removeControl('accountingContact')
      this.form.get('status').setValue(ClientStatus.Potential)
    }

    if (this.user.isClient()) {
      this.form.get('name').disable()
      this.form.get('legalName').disable()
      this.form.get('taxNr').disable()
      this.form.get('salesRepresentative').disable()
      this.form.get('accountManager').disable()
      this.form.get('expirationDate').disable()
      this.form.get('plan').disable()
      this.form.get('status').disable()
      this.form.get('chevronAccountId').disable()
    } else {
      this.form
        .get('status')
        .valueChanges.pipe(
          filter(() => !this.isBillingBlockedStatus(this.client?.status)),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          filter((status) => status !== this.form.value.status),
          takeUntil(this.onDestroy$),
        )
        .subscribe((status: ClientStatus) => this.changeValidationRules(status))
    }

    if (this.isContractMonth) {
      this.form.addControl('contractMonths', new FormControl(this.clientId ? '' : 36))
      this.user.isClient() && this.form.get('contractMonths').disable()
    }
    this.settingsService.isXeroEnabled && this.form.addControl('xeroContact', new FormControl())

    !this.isEditMode && this.form.disable()

    this.isFormChanged = takeFormSnapshot(this.form)
  }

  setFormValues() {
    const { client } = this
    const salesRepresentativeValue = client.salesManager
      ? this.user.isClient() || this.isManager
        ? client.salesManager.fullName
        : client.salesManager.id
      : null
    const accountManagerValue = client.manager
      ? this.user.isClient() || this.isManager
        ? client.manager.fullName
        : client.manager.id
      : null

    this.form.patchValue({
      name: client.name,
      legalName: client.legalName,
      country: client.country || '',
      taxNr: client.taxNr,
      chevronAccountId: client.chevronAccountId || '',
      legalAddress: client.legalAddress,
      billingAddress: client.billingAddress,
      status: client.status,
      plan: client.planId,
      keyContact: client.keyContactId || '',
      accountingContact: client.accountingContact?.id || '',
      ...(this.settingsService.isXeroEnabled && { xeroContact: this.clientXeroContact?.contactId }),
      timezone: client.timezone,
      ...(this.isContractMonth && { contractMonths: client.contractMonths || '' }),
      salesRepresentative: salesRepresentativeValue,
      accountManager: accountManagerValue,
      clientNote: '',
      adminNote: '',
      expirationDate: client.expirationDate ? dayjs(client.expirationDate).tz() : '',
      language: client.language,
    })

    this.isFormChanged = takeFormSnapshot(this.form)
  }

  changeValidationRules(status: ClientStatus) {
    // TODO: need to update!!
    if (status === ClientStatus.Client && this.form.get('keyContact')) {
      this.setRequired('keyContact')
    }

    if (status === ClientStatus.Client || status === ClientStatus.Blocked) {
      this.makeAllRequired()
    } else {
      Object.keys(this.form.controls).forEach((field) => this.clearValidators(field))
      this.setRequired('name')
    }

    if (status === ClientStatus.Demo) {
      this.setRequired('expirationDate')
    } else {
      this.clearValidators('expirationDate')
      this.form.get('expirationDate').setValue('')
    }

    if (this.isContractMonth) {
      this.clearValidators('contractMonths')
    }
    this.clearValidators('salesRepresentative')
    this.clearValidators('accountManager')
    this.clearValidators('country')
    this.clearValidators('adminNote')
    this.clearValidators('clientNote')
    this.clearValidators('billingAddress')
    this.clearValidators('taxNr')
    !this.showChevronAccountId && this.clearValidators('chevronAccountId')
    this.setRequired('plan')

    if (this.form.get('accountingContact')) {
      this.clearValidators('accountingContact')
    }

    this.settingsService.isXeroEnabled && this.clearValidators('xeroContact')

    this.formStatuses = this.statuses.map((s) => {
      if (this.isBillingBlockedStatus(<ClientStatus>s.name)) return s

      s.disabled = status === ClientStatus.Client ? s.name === ClientStatus.Demo : s.name === ClientStatus.Blocked
      return s
    })
  }

  makeAllRequired() {
    Object.keys(this.form.controls).forEach((field) => this.setRequired(field))
  }

  clearValidators(field: string): void {
    this.form.get(field).clearValidators()
    this.form.get(field).updateValueAndValidity()
    this.required.delete(field)
  }

  setRequired(field: string): void {
    this.form.get(field).setValidators([Validators.required])
    this.form.get(field).updateValueAndValidity()
    this.required.add(field)
  }

  getNotes() {
    this.clientsService
      .getClientNotes(this.clientId, 'client')
      .pipe(
        tap((notes) => {
          this.clientNotesHistory = notes
          this.cdr.markForCheck()
        }),
        takeUntil(this.onDestroy$),
        filter(() => this.user.isAdmin()),
        switchMap(() => this.clientsService.getClientNotes(this.clientId, 'admin')),
      )
      .subscribe((notes) => {
        this.adminNotesHistory = notes
        this.cdr.markForCheck()
      })
  }

  addNote(noteType: 'admin' | 'client', noteValue: string): void {
    this.clientsService
      .addClientNote(this.clientId, noteValue, noteType)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.getNotes()
      })
  }

  deleteNote(id: number): void {
    this.clientsService
      .deleteClientNote(id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.getNotes()
      })
  }

  getStatusHistory(): void {
    const dialogTitle = String(this.translateService.instant('ADMIN.DEVICES.STATUS_HISTORY'))

    this.isLoaded = false

    this.clientsService
      .getClientHistory(this.client.id, 'status')
      .pipe(filter(Boolean), takeUntil(this.onDestroy$))
      .subscribe((history) => {
        this.isLoaded = true
        this.cdr.markForCheck()

        this.openStatusHistoryDialog(history, dialogTitle)
      })
  }

  getContractHistory(): void {
    const dialogTitle = String(this.translateService.instant('ADMIN.CLIENTS.HISTORY'))
    this.isLoaded = false

    this.clientsService
      .getClientContractHistory(this.client.id, 1, 1000)
      .pipe(filter(Boolean), takeUntil(this.onDestroy$))
      .subscribe(({ data }: PagesData<History>) => {
        this.isLoaded = true
        this.cdr.markForCheck()

        this.openStatusHistoryDialog(data, dialogTitle, 'clientContract')
      })
  }

  openStatusHistoryDialog(history: History[], dialogTitle: string, type?: string): void {
    this.dialog.open(StatusHistoryDialogComponent, dialogTitle, { history, type })
  }

  saveClient(saveData: Client) {
    return this.client ? this.clientsService.updateClient(this.client.id, saveData) : this.clientsService.addClient(saveData)
  }

  saveXeroContact(client: Client): Observable<XeroContact[]> {
    const xeroContactValue = <string>this.form.get('xeroContact')?.value

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.settingsService.isXeroEnabled
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.clientsService.setClientXeroContact(xeroContactValue || '', client.id).pipe(
          switchMap(() => {
            const xeroContact$ = this.getClientXeroContact(client.id)
            const setBilling$ = xeroContactValue
              ? of(null)
              : this.settingsService.updateSettings(
                  [
                    {
                      roleId: null,
                      teamId: client.team.id,
                      name: 'billing',
                      value: false,
                    },
                  ],
                  client.team.id,
                  true,
                )

            return forkJoin([xeroContact$, setBilling$])
          }),
          tap(([xeroContact]) => (this.clientXeroContact = xeroContact ? xeroContact[0] : null)),
          switchMap(() => this.getXeroContactsRequest({ page: 1 })),
          tap((xeroContacts) => (this.xeroContacts = xeroContacts)),
        )
      : of(null)
  }

  saveData() {
    this.isSubmitting = true
    const saveData = this.prepareDataForSave() as Client
    const isCreate = this.user.isAdminOrReseller() && !this.client

    this.saveClient(saveData)
      .pipe(
        switchMap((client) => this.saveXeroContact(client).pipe(map(() => client))),
        switchMap((client) =>
          isCreate
            ? of(client)
            : this.clientsService.getClient(this.clientId).pipe(
                tap((fresh) => (this.client = fresh)),
                map(() => client),
              ),
        ),
        finalize(() => {
          this.isSubmitting = false
          this.cdr.markForCheck()
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe({
        next: (client) => {
          if (isCreate) {
            // CREATE branch redirects to the new-user screen -> reload+reset does not apply.
            this.isFormChanged = takeFormSnapshot(this.form)
            this.form.markAsPristine()
            void this.router.navigate([this.user.teamType, 'clients', client.id, 'users', 'new'])
            return
          }
          // EDIT branch stays on page: repopulate from fresh entity, re-baseline, mark pristine.
          this.setFormValues()
          this.form.markAsPristine()
        },
        error: () => handleFormErrors(this.form),
      })
  }

  prepareDataForSave() {
    const formData = this.form.value

    const data = {
      name: formData.name,
      legalName: formData.legalName,
      country: formData.country,
      taxNr: formData.taxNr,
      ...(this.showChevronAccountId && { chevronAccountId: formData.chevronAccountId }),
      legalAddress: formData.legalAddress,
      billingAddress: formData.billingAddress,
      status: formData.status,
      planId: formData.plan,
      timezone: formData.timezone,
      ...(this.isContractMonth && { contractMonths: formData.contractMonths }),
      ...(!this.isManager && {
        salesManager: formData.salesRepresentative,
        manager: formData.accountManager,
      }),
      clientNote: formData.clientNote,
      adminNote: formData.adminNote,
      language: formData.language,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      expirationDate: formData.status === ClientStatus.Demo ? dayjs(formData.expirationDate).tz(undefined, true).format() : null,
    }

    if (this.client) {
      data['keyContactId'] = formData.keyContact
      data['accountingContact'] = formData.accountingContact
    }

    return data
  }

  sendEmail(event: Event, fieldName: string) {
    event.stopPropagation()
    if (this.form.value[fieldName]) {
      const email = this.users.find((u) => u.id === this.form.value[fieldName]).email
      location.href = `mailto:${email}`
    }
  }

  isBillingBlockedStatus(status: ClientStatus): boolean {
    return [ClientStatus.PartiallyBlockedBilling, ClientStatus.BlockedBilling].includes(status)
  }

  getDropdownOptions(status: ClientStatus): ClientStatus[] {
    const defaultStatuses = [ClientStatus.Client, ClientStatus.Deleted, ClientStatus.Demo, ClientStatus.Potential, ClientStatus.Closed]

    if (status === ClientStatus.Client || status === ClientStatus.Demo) {
      return [...defaultStatuses, ClientStatus.Blocked, ClientStatus.BlockedOverdue]
    }

    return defaultStatuses
  }

  isStatusDisabled(status: ClientStatus): boolean {
    const allowedStatuses = this.getDropdownOptions(this.client?.status)
    return !allowedStatuses.includes(status)
  }

  getClientXeroContact(clientId: string | number): Observable<XeroContact[]> {
    return this.clientsService
      .getClientXeroContact(clientId)
      .pipe(
        switchMap((clientXeroContact) =>
          clientXeroContact.xeroContactId ? this.clientsService.getXeroContacts({ IDs: [clientXeroContact.xeroContactId] }) : of(null),
        ),
      )
  }

  displayXeroContact(xeroContact?: Pick<XeroContact, 'name' | 'client'>): string | null {
    return (xeroContact && `${xeroContact.name}${xeroContact.client ? ' (' + xeroContact.client.name + ')' : ''}`) || null
  }

  displayCountry(country?: { key: string; value: string }): string | null {
    return (country && country.value) || null
  }

  handleXeroContactSelect(xeroContactId: string): void {
    this.form.get('xeroContact').patchValue(xeroContactId)
    this.form.markAsDirty()
  }

  handleCountrySelect(countryKey: string): void {
    this.form.get('country').patchValue(countryKey)
    this.form.markAsDirty()
  }

  ngOnDestroy() {
    this.unsavedFormTrackerService.discard(this)
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
